import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import localization from '../../shared/strings';
import { showDialog } from '../dialog';
import { customPreset } from '../DialogPresets/config';
import * as downloadPresetActions from '../DownloadDialog/actions';
import canShowCropPreview from '../../helpers/assets/canShowCropPreview';
import isCropSupportedFormat from '../../helpers/assets/isCropSupportedFormat';

/**
 * Default social presets (Instagram/Facebook/X/LinkedIn/YouTube) carry a curated
 * `ratios` array — each entry describing a ratio key (e.g. '4:5'), width, height
 * and optional `tooltip` / `safeArea` / `isDefault`. Helpers below let the form
 * auto-apply the default ratio when such a preset is selected and keep
 * `selectedSettings.width/height` in sync when the user picks a different chip.
 */
function getDefaultRatio(ratios) {
  if (!Array.isArray(ratios) || ratios.length === 0) return null;
  return ratios.find((r) => r && r.isDefault) || ratios[0];
}

function applyRatioToSettings(settings, ratio) {
  if (!ratio) return settings;
  const next = { ...settings };
  if (typeof ratio.width === 'number') next.width = ratio.width;
  if (typeof ratio.height === 'number') next.height = ratio.height;
  return next;
}

/**
 * Encapsulates state & actions for the unified conversion dialog (copy/download).
 * UI stays presentational: it consumes the returned bag via {@link ConversionDialogShell}.
 *
 * The `strategy` argument (see `conversionDialogStrategies.js`) decides which presets
 * to build, how to format strings and how to shape the submit payload.
 */
export default function useAssetConversionForm({
  strategy,
  shouldFormatBeDisabled,
  assets,
  selectionCount,
  copyPreviewAssetId,
  downloadPresets,
  updateDownloadPresets,
  subscriptionFeatures,
  onSubmit,
  onClose,
  forceOriginalOnly = false,
}) {
  const previewAsset = useSelector((state) => {
    if (!copyPreviewAssetId || selectionCount !== 1) return null;
    if (Array.isArray(assets)) {
      const fromProps = assets.find((a) => a && a._id === copyPreviewAssetId);
      if (fromProps) return fromProps;
    }
    const items = state && state.assets && state.assets.items;
    if (Array.isArray(items)) {
      return items.find((a) => a && a._id === copyPreviewAssetId) || null;
    }
    return null;
  });

  const canPreviewCrop = useMemo(
    () => (previewAsset ? canShowCropPreview(previewAsset) : false),
    [previewAsset],
  );

  const cropApplies = useMemo(() => {
    if (!previewAsset) return true;
    return isCropSupportedFormat(previewAsset.mimeType, previewAsset.fileExtension);
  }, [previewAsset]);

  const bulkCropApplies = useMemo(() => {
    if (!Array.isArray(assets) || !assets.length) return true;
    return assets.every((a) => isCropSupportedFormat(a && a.mimeType, a && a.fileExtension));
  }, [assets]);

  /** Crop UI only — do not use this to filter conversion presets (bulk mixed croppable vs convertible). */
  const cropSupported = selectionCount > 1 ? bulkCropApplies : cropApplies;

  const presetBundle = useMemo(
    () => strategy.buildPresets({
      downloadPresets,
      shouldFormatBeDisabled,
      assets,
      selectionCount,
      forceOriginalOnly,
    }),
    [assets, downloadPresets, selectionCount, shouldFormatBeDisabled, strategy, forceOriginalOnly],
  );
  const {
    presets: rawPresets,
    hasPdfProxyPresets,
    hasCustomVideoPresets,
  } = presetBundle;

  const presets = rawPresets;

  const defaultPreset = presets[0];
  const initialRatio = getDefaultRatio(defaultPreset?.data?.ratios);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState(
    defaultPreset?._id || customPreset._id,
  );
  const [selectedSettings, setSelectedSettings] = useState(
    applyRatioToSettings(defaultPreset?.data || customPreset.data, initialRatio),
  );
  const [normalizedCrop, setNormalizedCrop] = useState(null);
  const [cropAspectPreset, setCropAspectPreset] = useState(
    initialRatio?.ratio || 'original',
  );
  const [cropPixelSize, setCropPixelSize] = useState(null);
  const [cropSourceNaturalSize, setCropSourceNaturalSize] = useState(null);
  const [cropMode, setCropMode] = useState('preset');
  const cropPreviewRef = useRef(null);

  const isAssetsWithWatermark = useMemo(
    () => (Array.isArray(assets) ? assets.some((a) => a?.watermarkId) : false),
    [assets],
  );

  const selectedPreset = useMemo(
    () => presets.find(({ _id }) => _id === selectedPresetId) || defaultPreset || customPreset,
    [defaultPreset, presets, selectedPresetId],
  );
  const selectedPresetView = useMemo(
    () => ({ ...selectedPreset, data: selectedSettings }),
    [selectedPreset, selectedSettings],
  );

  const isSplitCropLayout = Boolean(
    !shouldFormatBeDisabled
    && !forceOriginalOnly
    && copyPreviewAssetId
    && selectionCount === 1
    && canPreviewCrop
    && cropApplies,
  );

  const showCropControls = cropSupported && (selectionCount > 1 || canPreviewCrop);

  const showPresets = strategy.canShowPresets({ hasPdfProxyPresets, forceOriginalOnly });
  const showConversionSettings = strategy.canShowConversionSettings({
    shouldFormatBeDisabled,
    selectedPreset,
    hasPdfProxyPresets,
    hasCustomVideoPresets,
    forceOriginalOnly,
  });
  const showExtraPanel = strategy.canShowExtraPanel({ selectionCount, forceOriginalOnly });

  useEffect(() => {
    setCropAspectPreset('original');
    setCropPixelSize(null);
    setCropSourceNaturalSize(null);
    setCropMode('preset');
  }, [copyPreviewAssetId, selectionCount]);

  useEffect(() => {
    if (!presets.some(({ _id }) => _id === selectedPresetId)) {
      const fallback = defaultPreset || customPreset;
      const ratio = getDefaultRatio(fallback.data?.ratios);
      setSelectedPresetId(fallback._id);
      setSelectedSettings(applyRatioToSettings({ ...fallback.data }, ratio));
      if (ratio?.ratio) setCropAspectPreset(ratio.ratio);
    }
  }, [defaultPreset, presets, selectedPresetId]);

  // Restore saved crop fields attached by `handleSavePreset` when a preset is selected.
  useEffect(() => {
    const preset = presets.find(({ _id }) => _id === selectedPresetId);
    const d = preset?.data;
    if (!d?.savedCropAspectPreset) return;
    setCropAspectPreset(d.savedCropAspectPreset);
    if (isSplitCropLayout && d.savedCropPixelWidth && d.savedCropPixelHeight) {
      setCropMode('manual');
      queueMicrotask(() => {
        cropPreviewRef.current?.applyFromNaturalPixels(
          d.savedCropPixelWidth,
          d.savedCropPixelHeight,
        );
      });
    }
  }, [selectedPresetId, presets, isSplitCropLayout]);

  const setPresetValue = useCallback((name, value) => {
    setSelectedSettings((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChangePreset = useCallback((e) => {
    const { target: { value } } = e;
    const preset = presets.find(({ _id }) => _id === value);
    if (!preset) return;

    setSelectedPresetId(preset._id);
    const ratio = getDefaultRatio(preset.data?.ratios);
    setSelectedSettings(applyRatioToSettings({ ...preset.data }, ratio));

    if (ratio?.ratio) {
      setCropAspectPreset(ratio.ratio);
      setCropMode('preset');
    } else if (!preset.data?.savedCropAspectPreset) {
      setCropAspectPreset('original');
      setCropMode('preset');
    }
  }, [presets]);

  const handleChangeSettings = useCallback((e) => {
    const {
      target: {
        type, name, value, checked,
      },
    } = e;
    let validValue = value;

    switch (type) {
    case 'number':
      validValue = value === '' ? '' : Number(value);
      break;
    case 'checkbox':
      validValue = checked;
      if (name === 'isArchive' && !checked) {
        setPresetValue('organizeByCollections', false);
      }
      if (name === 'withoutWatermark' && !checked) {
        setPresetValue('organizeByCollections', false);
      }
      break;
    default:
      break;
    }
    setPresetValue(name, validValue);
  }, [setPresetValue]);

  const handleBlurSettings = useCallback((e) => {
    const { target: { type, name, value } } = e;
    if (type === 'number' && !Number(value)) {
      setPresetValue(name, 1);
    }
  }, [setPresetValue]);

  const handleCropAspectPresetChange = useCallback((key) => {
    setCropMode('preset');
    setCropAspectPreset(key);

    // When the current preset carries a curated `ratios` list, picking a ratio
    // chip must also update width/height so the submit payload and preview
    // reflect the new target size.
    const ratios = selectedPreset?.data?.ratios;
    if (Array.isArray(ratios)) {
      const ratio = ratios.find((r) => r && r.ratio === key);
      if (ratio) {
        setSelectedSettings((prev) => applyRatioToSettings(prev, ratio));
      }
    }
  }, [selectedPreset]);

  const handleCropPixelsCommit = useCallback((w, h) => {
    setCropAspectPreset('free');
    setCropMode('manual');
    queueMicrotask(() => {
      cropPreviewRef.current?.applyFromNaturalPixels(w, h);
    });
  }, []);

  const handleUserAdjustedCrop = useCallback(() => {
    // Default social presets lock the crop rect to a curated ratio/size —
    // only dragging (reposition) is allowed in the preview, resize handles are
    // hidden. Keep the ratio chip highlighted and Crop width/height fields
    // intact in that case; the updated normalized crop rect still flows through
    // `onNormalizedCropChange` for submit.
    const isLockedPreset = Array.isArray(selectedPreset?.data?.ratios)
      && selectedPreset.data.ratios.length > 0;
    if (isLockedPreset) return;

    setCropMode('manual');
    setCropAspectPreset('free');
  }, [selectedPreset]);

  const savePreset = useCallback(async (name, shared, isNew) => {
    const { _id } = selectedPreset;

    try {
      setIsLoading(true);
      const requestData = {
        name,
        data: {
          ...selectedSettings,
          savedCropAspectPreset: cropAspectPreset,
          ...(cropPixelSize && {
            savedCropPixelWidth: cropPixelSize.width,
            savedCropPixelHeight: cropPixelSize.height,
          }),
        },
        shared,
      };
      const preset = isNew
        ? await downloadPresetActions.createDownloadPreset(requestData)
        : await downloadPresetActions.updateDownloadPreset({ _id, ...requestData });

      const next = downloadPresets.some(({ _id: id }) => id === preset._id)
        ? downloadPresets.map((item) => (item._id === preset._id ? preset : item))
        : [...downloadPresets, preset];

      updateDownloadPresets(next);
      setSelectedPresetId(preset._id);
      setSelectedSettings({ ...preset.data });
    } catch (err) {
      showDialog({
        title: localization.ERROR,
        text: strategy.presetDialogStrings.saveError(),
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    cropAspectPreset,
    cropPixelSize,
    downloadPresets,
    selectedPreset,
    selectedSettings,
    strategy.presetDialogStrings,
    updateDownloadPresets,
  ]);

  const handleSavePreset = useCallback(() => {
    const { _id, name, teamId: isShared } = selectedPresetView;
    const isNew = _id === customPreset._id;
    const presetName = isNew ? '' : name;
    const title = isNew
      ? strategy.presetDialogStrings.createPresetTitle()
      : strategy.presetDialogStrings.savePresetTitle();

    showDialog({
      title,
      input: { label: 'Name', type: 'text', value: presetName },
      checkbox: { value: !!isShared, label: 'Share with my team' },
      disableOk: ({ input }) => !input,
      onOk: ({ input: nextName, checkbox: shared }) => {
        savePreset(nextName, shared, isNew);
      },
    });
  }, [savePreset, selectedPresetView, strategy.presetDialogStrings]);

  const deletePreset = useCallback(async (presetId) => {
    try {
      setIsLoading(true);
      await downloadPresetActions.deleteDownloadPreset(presetId);
      const next = downloadPresets.filter(({ _id }) => _id !== presetId);
      updateDownloadPresets(next);
      if (selectedPresetId === presetId) {
        const fallback = (presets.find((p) => p._id !== presetId)) || customPreset;
        setSelectedPresetId(fallback._id);
        setSelectedSettings({ ...fallback.data });
      }
    } catch (err) {
      showDialog({
        title: localization.ERROR,
        text: strategy.presetDialogStrings.removeError(),
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    downloadPresets,
    presets,
    selectedPresetId,
    strategy.presetDialogStrings,
    updateDownloadPresets,
  ]);

  const handleRemovePreset = useCallback((presetId, presetName) => {
    showDialog({
      title: strategy.presetDialogStrings.removePresetTitle(),
      text: strategy.presetDialogStrings.removePresetText(presetName),
      onOk: () => deletePreset(presetId),
    });
  }, [deletePreset, strategy.presetDialogStrings]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    const effectiveNormalizedCrop = showCropControls ? normalizedCrop : null;
    const effectiveCropAspectPreset = showCropControls ? cropAspectPreset : 'original';

    const payload = strategy.buildSubmitPayload({
      selectedPresetId,
      selectedSettings,
      normalizedCrop: effectiveNormalizedCrop,
      cropAspectPreset: effectiveCropAspectPreset,
      selectionCount,
      copyPreviewAssetId,
      subscriptionFeatures,
      isAssetsWithWatermark,
      presets,
      forceOriginalOnly,
    });
    onSubmit?.(payload, { selectedPresetId });
  }, [
    copyPreviewAssetId,
    cropAspectPreset,
    forceOriginalOnly,
    isAssetsWithWatermark,
    normalizedCrop,
    onSubmit,
    presets,
    selectedPresetId,
    selectedSettings,
    selectionCount,
    showCropControls,
    strategy,
    subscriptionFeatures,
  ]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.keyCode === 27) handleClose();
      else if (e.keyCode === 13) handleSubmit();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, handleSubmit]);

  return {
    // presets & settings
    presets,
    selectedPresetId,
    selectedPresetView,
    selectedSettings,
    isLoading,
    hasPdfProxyPresets,
    hasCustomVideoPresets,
    cropAspectPreset,
    cropPixelSize,
    cropSourceNaturalSize,
    cropMode,
    cropPreviewRef,
    setNormalizedCrop,
    setCropPixelSize,
    setCropSourceNaturalSize,
    customRatios: Array.isArray(selectedPreset?.data?.ratios)
      ? selectedPreset.data.ratios
      : null,
    isAssetsWithWatermark,
    isSplitCropLayout,
    showPresets,
    showConversionSettings,
    showCropControls,
    showExtraPanel,
    cropSupported,

    // handlers
    handleChangePreset,
    handleChangeSettings,
    handleBlurSettings,
    handleSavePreset,
    handleRemovePreset,
    handleCropAspectPresetChange,
    handleCropPixelsCommit,
    handleUserAdjustedCrop,
    handleSubmit,
    handleClose,
  };
}
